// Click-to-place. While in placement mode, a translucent HEX ghost tracks the
// mouse over the field, tinted by whether the hovered tile is a legal home for
// the selected building (owned + correctly zoned + free). Left-click commits;
// ESC cancels. Emits PlacementCommit so buildingManager handles spendWealth +
// the zoning rules (it fines the player if they insist on a wrong-zone tile).

import * as THREE from 'three'
import { Events, on, emit } from '../ui/eventBus.js'
import { BuildingConfig, PlotTypes, RicePaddyPrice } from '../config/balance.js'
import { isTileBuilt } from '../game/buildingState.js'
import { getPlotType, isOwned, isWild, plotPrice, claimPlot, setPlot } from '../game/plotState.js'
import { spendWealth, getWealth } from '../game/seasonManager.js'
import { formatCoins, cantAfford } from '../ui/format.js'
import { pushLog } from '../ui/hud.js'
import { createHexTileGeometry, HEX_SIZE, GROUND_Y } from './hexGrid.js'
import { getPending } from '../game/buildingManager.js'

// Special placement "types" that aren't buildings: claim wild land, or zone an
// owned plot into a rice paddy. Everything else is a BuildingConfig type.
const LAND = 'LAND'
const RICE_PADDY = 'RICE_PADDY'

const VALID_COLOR = 0x6ad17a   // green — legal build target
const WRONG_COLOR = 0xc8484c   // red — owned but wrong zone / already built
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()

let sceneRef = null
let cameraRef = null
let tilesRef = []
let canvasEl = null
let bannerEl = null
let ghost = null
let activeType = null

// Exposed so heroController can suppress its click-to-move handler while the
// player is in the middle of placing a building.
export function isPlacementActive() {
  // Also true while a dropped building awaits Confirm / Reposition / Cancel, so those clicks don't send the cat walking.
  return activeType !== null || !!getPending()
}

// Where a tile (or a building on it) is on screen, in client pixels — the confirm/rotate chip follows it.
const tileMeshByKey = new Map()
export function tileScreenPos(tileKey, lift = 1.0) {
  const tile = tileMeshByKey.get(tileKey)
  if (!tile || !cameraRef || !canvasEl) return null
  const v = new THREE.Vector3(tile.position.x, GROUND_Y + lift, tile.position.z).project(cameraRef)
  const r = canvasEl.getBoundingClientRect()
  return { x: r.left + ((v.x + 1) / 2) * r.width, y: r.top + ((1 - v.y) / 2) * r.height }
}

// The cursor while placing: a thin yellow square with a + crosshair, and a hint beside it.
let cursorEl = null
function buildCursor() {
  const el = document.createElement('div')
  el.className = 'place-cursor'
  el.innerHTML = '<span class="place-cursor__sq"><i></i><i></i></span><span class="place-cursor__tip"></span>'
  document.body.appendChild(el)
  return el
}
const cursorTip = (txt, bad = false) => {
  const t = cursorEl?.querySelector('.place-cursor__tip')
  if (!t) return
  if (t.textContent !== txt) t.textContent = txt
  cursorEl.classList.toggle('bad', bad)
}

export function createPlacement({ scene, camera, tiles, canvas, banner }) {
  sceneRef = scene
  cameraRef = camera
  tilesRef = tiles
  canvasEl = canvas
  bannerEl = banner
  for (const t of tiles) tileMeshByKey.set(t.userData.tileIndex, t)
  cursorEl = buildCursor()

  on(Events.PlacementStart, ({ type }) => start(type))
  on(Events.PlacementCancel, () => stop())
  on(Events.PlacementCommit, () => stop())
  on(Events.KingdomChange, () => stop())

  canvas.addEventListener('pointermove', onPointerMove)
  canvas.addEventListener('pointerleave', () => cursorEl?.classList.remove('on'))
  canvas.addEventListener('click', onClick)
  window.addEventListener('keydown', onKey)
}

function onKey(e) {
  if (e.key === 'Escape' && activeType) emit(Events.PlacementCancel)
}

function start(type) {
  stop()
  // Accept buildings + the two special modes (LAND claim, RICE_PADDY zone).
  if (type !== LAND && type !== RICE_PADDY && !BuildingConfig[type]) return
  activeType = type

  // A flat hex ghost the size of one tile — reads as "this tile" rather than a
  // mystery box, and tints green/red as the player sweeps the field.
  const geo = createHexTileGeometry(HEX_SIZE * 0.97, 0.12)
  const mat = new THREE.MeshBasicMaterial({ color: VALID_COLOR, transparent: true, opacity: 0.4, depthWrite: false })
  ghost = new THREE.Mesh(geo, mat)
  ghost.position.y = GROUND_Y + 0.04
  ghost.visible = false
  ghost.renderOrder = 3
  sceneRef.add(ghost)

  canvasEl.style.cursor = 'none' // the yellow-square cursor takes over
  cursorEl?.classList.add('on')
  cursorTip(type === LAND ? 'Click a wild tile to buy it' : 'Place on a purchased plot of land')
  cursorEl?.querySelector('.place-cursor__tip')?.setAttribute('data-sub', 'ESC to cancel')
  if (bannerEl && type === LAND) { // claiming land keeps its live price banner
    bannerEl.textContent = startBanner(type)
    bannerEl.classList.add('active')
  }
}

function startBanner(type) {
  if (type === LAND) return 'Buy land — click a wild tile (cheaper toward the edges) · ESC when done'
  if (type === RICE_PADDY) return `Place a Rice Paddy on one of your plots (${formatCoins(RicePaddyPrice)}) · ESC when done`
  return `Place ${BuildingConfig[type]?.name} on one of your plots — ESC to cancel`
}

function stop() {
  if (ghost) {
    sceneRef.remove(ghost)
    ghost.geometry.dispose()
    ghost.material.dispose()
    ghost = null
  }
  activeType = null
  if (canvasEl) canvasEl.style.cursor = ''
  cursorEl?.classList.remove('on')
  if (bannerEl) bannerEl.classList.remove('active')
}

// Resolve the hovered tile's hex key (the mesh carries it in userData) + its
// world-space mesh, or null off the field.
function raycastTile(event) {
  const rect = canvasEl.getBoundingClientRect()
  pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  raycaster.setFromCamera(pointer, cameraRef)
  const hits = raycaster.intersectObjects(tilesRef, false)
  if (!hits.length) return null
  const mesh = hits[0].object
  return { mesh, key: mesh.userData?.tileIndex }
}

// Is `key` a legal target for the active mode?
//   LAND        → an unclaimed wild tile (you buy it).
//   RICE_PADDY  → an owned, undeveloped (GRASS) plot.
//   building    → an owned plot that's undeveloped GRASS (auto-zoned on commit)
//                 or already the right zone — never a wrong developed zone.
function isValidTarget(key) {
  if (key == null) return false
  if (activeType === LAND) return isWild(key)
  if (isTileBuilt(key) || !isOwned(key)) return false
  const plot = getPlotType(key)
  if (activeType === RICE_PADDY) return plot === PlotTypes.GRASS
  const allowed = BuildingConfig[activeType]?.allowedPlotTypes
  return plot === PlotTypes.GRASS || !allowed || allowed.includes(plot)
}

function onPointerMove(event) {
  if (!activeType || !ghost) return
  if (cursorEl) { cursorEl.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`; cursorEl.classList.add('on') }
  const hit = raycastTile(event)
  if (!hit || hit.key == null) { ghost.visible = false; return }
  ghost.visible = true
  ghost.position.x = hit.mesh.position.x
  ghost.position.z = hit.mesh.position.z
  const ok = isValidTarget(hit.key)
  ghost.material.color.setHex(ok ? VALID_COLOR : WRONG_COLOR)
  if (activeType !== LAND) cursorTip(ok ? 'Place on a purchased plot of land' : (isOwned(hit.key) ? 'That plot is already in use' : 'Not a purchased plot'), !ok)
  // In claim mode the banner shows the live price of the hovered wild tile.
  if (activeType === LAND && bannerEl) {
    bannerEl.textContent = ok
      ? `Buy this plot for ${formatCoins(plotPrice(hit.key))} · click to claim · ESC when done`
      : 'Buy land — hover a wild (lighter green) tile · ESC when done'
  }
}

function onClick(event) {
  if (!activeType) return
  const hit = raycastTile(event)
  if (!hit || hit.key == null || !isValidTarget(hit.key)) return

  if (activeType === LAND) {
    const res = claimPlot(hit.key, spendWealth)
    if (res.ok) pushLog(`Claimed a plot for ${formatCoins(res.cost)}. Now place a building or rice paddy on it.`)
    else pushLog(res.cost ? cantAfford('claim that plot', res.cost, getWealth()) : 'You cannot claim that plot.')
    return // stay in claim mode — buy as many as you like; ESC to finish
  }

  if (activeType === RICE_PADDY) {
    if (!spendWealth(RicePaddyPrice)) { pushLog(cantAfford('lay a rice paddy', RicePaddyPrice, getWealth())); return }
    setPlot(hit.key, PlotTypes.FARM)
    pushLog(`Rice paddy laid for ${formatCoins(RicePaddyPrice)} — it earns every harvest.`)
    return // stay in mode
  }

  // A building — hand off to buildingManager (spends coin, auto-zones, places).
  emit(Events.PlacementCommit, { type: activeType, tileIndex: hit.key })
}
